import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Send, CheckCircle2, ExternalLink, ChevronLeft } from 'lucide-react';
import axios from 'axios';

import { API_BASE } from '../lib/api-base';
const API = `${API_BASE}/api`;

export default function LeadForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const summaryData = location.state || {};
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', postalCode: '', message: '', privacyAccepted: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const up = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const canSubmit = form.name && form.email && form.postalCode && form.privacyAccepted;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/leads`, { ...form, module: summaryData.module || 'direct', technicalSummary: summaryData.summary || null });
      setSubmitted(true);
    } catch { setError('Beim Senden ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1 py-8 sm:py-12">
        <div className="ees-container max-w-lg">
          {submitted ? (
            <div className="text-center py-12 ees-step-enter" data-testid="lead-success">
              <CheckCircle2 className="w-16 h-16 text-[#9ec043] mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-[#32373c] mb-2">Vielen Dank für Ihre Anfrage!</h2>
              <p className="text-sm text-gray-500 mb-8">Wir melden uns zeitnah bei Ihnen.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate('/')} className="bg-[#9ec043] hover:bg-[#8aad38] text-white" data-testid="btn-back-home">Zum Konfigurator</Button>
                <Button variant="outline" asChild className="text-gray-500 border-gray-300" data-testid="btn-success-ees">
                  <a href="https://www.ees-energiespeicher.de/" target="_blank" rel="noopener noreferrer">Zurück zur EES-Startseite <ExternalLink className="w-4 h-4 ml-1" /></a>
                </Button>
              </div>
            </div>
          ) : (
            <div className="ees-step-enter">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#32373c] mb-2">Beratung anfragen</h2>
              <p className="text-sm text-gray-500 mb-6">Hinterlassen Sie uns Ihre Kontaktdaten und wir melden uns bei Ihnen.</p>
              {summaryData.summary && (
                <Accordion type="single" collapsible className="mb-6">
                  <AccordionItem value="summary" className="border-gray-200">
                    <AccordionTrigger className="text-sm text-gray-500 hover:no-underline">Ihre Konfiguration im Überblick</AccordionTrigger>
                    <AccordionContent className="text-xs text-gray-400"><pre className="whitespace-pre-wrap break-words">{JSON.stringify(summaryData.summary?.result || summaryData.summary, null, 2).slice(0, 1000)}</pre></AccordionContent>
                  </AccordionItem>
                </Accordion>
              )}
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="lead-form">
                <div><Label className="text-gray-600 text-sm">Name *</Label><Input value={form.name} onChange={e => up('name', e.target.value)} placeholder="Ihr Name" className="mt-1 bg-white border-gray-300" required data-testid="input-lead-name" /></div>
                <div><Label className="text-gray-600 text-sm">Firma (optional)</Label><Input value={form.company} onChange={e => up('company', e.target.value)} placeholder="Firmenname" className="mt-1 bg-white border-gray-300" data-testid="input-lead-company" /></div>
                <div><Label className="text-gray-600 text-sm">E-Mail *</Label><Input type="email" value={form.email} onChange={e => up('email', e.target.value)} placeholder="ihre@email.de" className="mt-1 bg-white border-gray-300" required data-testid="input-lead-email" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-gray-600 text-sm">Telefon (optional)</Label><Input value={form.phone} onChange={e => up('phone', e.target.value)} placeholder="+49..." className="mt-1 bg-white border-gray-300" data-testid="input-lead-phone" /></div>
                  <div><Label className="text-gray-600 text-sm">Postleitzahl *</Label><Input value={form.postalCode} onChange={e => up('postalCode', e.target.value)} placeholder="z. B. 08141" className="mt-1 bg-white border-gray-300" required data-testid="input-lead-plz" /></div>
                </div>
                <div><Label className="text-gray-600 text-sm">Nachricht (optional)</Label><Textarea value={form.message} onChange={e => up('message', e.target.value)} placeholder="Beschreiben Sie Ihr Projekt..." className="mt-1 bg-white border-gray-300 min-h-[80px]" data-testid="input-lead-message" /></div>
                <div className="flex items-start gap-2 pt-2">
                  <Checkbox checked={form.privacyAccepted} onCheckedChange={v => up('privacyAccepted', !!v)} id="privacy" data-testid="check-privacy" />
                  <Label htmlFor="privacy" className="text-xs text-gray-500 cursor-pointer leading-relaxed">
                    Ich stimme der Verarbeitung meiner Daten gemäß der{' '}<a href="https://www.ees-energiespeicher.de/datenschutz/" target="_blank" rel="noopener noreferrer" className="text-[#9ec043] underline">Datenschutzerklärung</a> zu. *
                  </Label>
                </div>
                {error && <p className="text-sm text-red-600" data-testid="lead-error">{error}</p>}
                <Button type="submit" disabled={!canSubmit || submitting} className="w-full bg-[#9ec043] hover:bg-[#8aad38] text-white py-3" data-testid="btn-submit-lead">
                  <Send className="w-4 h-4 mr-2" />{submitting ? 'Wird gesendet...' : 'Anfrage senden'}
                </Button>
              </form>
              <div className="mt-6 pt-4 border-t border-gray-100 flex justify-between">
                <Button variant="ghost" onClick={() => {
                  const mod = summaryData.module;
                  const summary = summaryData.summary;
                  if (mod === 'pv' && summary) {
                    navigate('/pv-konfigurator', { state: { restore: true, mode: summary.mode, entries: summary.entries, result: summary.result } });
                  } else if (mod === 'general' && summary) {
                    navigate('/konfigurator', { state: { restore: true, data: summary } });
                  } else {
                    navigate(-1);
                  }
                }} className="text-gray-500" data-testid="btn-lead-back"><ChevronLeft className="w-4 h-4 mr-1" /> Zurück</Button>
                <a href="https://www.ees-energiespeicher.de/" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-[#9ec043] flex items-center gap-1 transition-colors" data-testid="btn-lead-ees">EES-Startseite <ExternalLink className="w-3.5 h-3.5" /></a>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
